
import React, { useState, useEffect } from "react";
import Navbar from "@/components/Navbar";
import SolutionFilters from "@/components/agency-solutions/SolutionFilters";
import ShoppingCartComponent from "@/components/agency-solutions/ShoppingCart";
import SolutionsHeader from "@/components/agency-solutions/SolutionsHeader";
import SolutionsContent from "@/components/agency-solutions/SolutionsContent";
import { solutionsData } from "@/data/solutionsData";
import { PRICE_FACTORS, Solution } from "@/types/solutions";
import { getAgencyColor, getCommercialTypeColor } from "@/utils/solutionUtils";

const AgencySolutions = () => {
  const [selectedAgency, setSelectedAgency] = useState<string>("all");
  const [commercialType, setCommercialType] = useState<string>("Sale");
  const [cart, setCart] = useState<Solution[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewType, setViewType] = useState("list");
  const [solutions, setSolutions] = useState<Solution[]>([]);

  // Update prices when commercial type changes
  useEffect(() => {
    const updatedSolutions = solutionsData.map(solution => ({
      ...solution,
      commercialType: commercialType as "Sale" | "Lease" | "Collaborate" | "Stake",
      price: Math.round(solution.basePrice * PRICE_FACTORS[commercialType as keyof typeof PRICE_FACTORS])
    }));
    setSolutions(updatedSolutions);
  }, [commercialType]);

  // Initialize solutions on component mount
  useEffect(() => {
    setSolutions(solutionsData);
  }, []);

  // Filter solutions based on selected criteria
  const filteredSolutions = solutions.filter((solution) => {
    return (
      (selectedAgency === "all" || solution.agency === selectedAgency) &&
      (
        solution.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        solution.creator.toLowerCase().includes(searchQuery.toLowerCase()) ||
        solution.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
        solution.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
  });

  const addToCart = (solution: Solution) => {
    if (!cart.some(item => item.id === solution.id)) {
      setCart([...cart, solution]);
    }
  };

  const removeFromCart = (id: string) => {
    setCart(cart.filter(item => item.id !== id));
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <SolutionsHeader cartItemCount={cart.length} />

        <div className="grid grid-cols-12 gap-6">
          {/* Filters & Cart - Left Column */}
          <div className="col-span-3">
            <div className="space-y-6">
              <SolutionFilters
                selectedAgency={selectedAgency}
                setSelectedAgency={setSelectedAgency}
                commercialType={commercialType}
                setCommercialType={setCommercialType}
              />
              
              <ShoppingCartComponent
                cart={cart}
                removeFromCart={removeFromCart}
              />
            </div>
          </div>

          {/* Main Content - Right Column (wider now) */}
          <div className="col-span-9">
            <SolutionsContent
              solutions={filteredSolutions}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              viewType={viewType}
              setViewType={setViewType}
              addToCart={addToCart}
              cart={cart}
              getAgencyColor={getAgencyColor}
              getCommercialTypeColor={getCommercialTypeColor}
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default AgencySolutions;
